import { User } from "Core/oRPC";
import { observer } from "mobx-react";
import React, { useMemo } from "react";
import { useInRouterContext } from "react-router";
import { M365PulseLink } from "Shared/Components";
import { useInLoggedInStatus, usePersonImage } from "Shared/Hooks";
import { useRootStore } from "Store/RootStore";

import { Avatar, AvatarProps, Divider, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Persona } from "@fluentui/react-components";

import { useStyles as useContainerStyles } from "./AppMenuIconButton.styles";
import { useStyles } from "./UserDropDown.styles";
import { UserDropDownCachedUsers } from "./UserDropDownCachedUsers";
import { UserDropDownItem } from "./UserDropDownItem";
import { useUserDropDownItems } from "./useUserDropDownItems";

const UserDropDownBase = () => {
    const classNames = useStyles();
    const containerClass = useContainerStyles();
    const { profile } = useRootStore("MyStore");
    const isLoggedIn = useInLoggedInStatus();
    const isInRouter = useInRouterContext();

    const dropdownItems = useUserDropDownItems();

    const { personImageUrl: personImageURL } = usePersonImage(profile.Alias ?? "", isLoggedIn);

    const personImage = useMemo(() => ({ src: personImageURL }), [personImageURL]);

    const initials = useMemo(() => getInitials(profile), [profile]);

    const avatar: AvatarProps = useMemo(
        () => ({
            image: {
                src: personImageURL
            },
            initials: initials,
            size: 72
        }),
        [initials, personImageURL]
    );

    const renderMenuList = (
        <div className={classNames.userMenu}>
            <MenuItem className={classNames.personaItem}>
                <Persona
                    avatar={avatar}
                    primaryText={<div className={classNames.name}>{profile.DisplayName ?? undefined}</div>}
                    size="huge"
                    secondaryText={<div className={classNames.textMessage}>{profile.Email}</div>}
                    tertiaryText={
                        <M365PulseLink path="Contacts" className={classNames.link}>
                            Contacts
                        </M365PulseLink>
                    }
                    quaternaryText={
                        <M365PulseLink path="/idm/identity/access/Overview" className={classNames.link} goToOSPV1>
                            Identity
                        </M365PulseLink>
                    }
                />
            </MenuItem>
            <UserDropDownCachedUsers />
            {dropdownItems.map((item) => (item.key.includes("divider") ? <Divider key={item.key} /> : <UserDropDownItem key={item.key} item={item} />))}
        </div>
    );

    return isLoggedIn && isInRouter ? (
        <div>
            <Menu positioning="below-end">
                <MenuTrigger>
                    <div className={containerClass.root} title="profile" aria-label="profile" role="button">
                        <Avatar image={personImage} initials={initials} size={32} aria-label="profile photo" />
                    </div>
                </MenuTrigger>
                <MenuPopover className={classNames.popover}>
                    <MenuList>{renderMenuList}</MenuList>
                </MenuPopover>
            </Menu>
        </div>
    ) : (
        <div className={containerClass.root}>
            <Avatar size={32} aria-label="profile photo" />
        </div>
    );
};

export const UserDropDown = observer(UserDropDownBase);

function getInitials(profile: User) {
    let firstChar = "";
    let lastChar = "";
    if (profile.FirstName || profile.LastName) {
        firstChar = profile.FirstName ? profile.FirstName.charAt(0) : "";
        lastChar = profile.LastName ? profile.LastName.charAt(0) : "";
    } else if (profile.DisplayName) {
        firstChar = profile.DisplayName.split(" ")[0]?.charAt(0) ?? "";
        lastChar = profile.DisplayName.split(" ")[1]?.charAt(0) ?? "";
    }

    return firstChar + lastChar;
}
